import { URLS } from "@/lib/constants";
import { UserBookmark, UserFolder } from "@/supabase/services/userFolders";
import { Platform } from "react-native";
import * as Haptics from "expo-haptics";

const PUBLISHED_STATUS = 3;

export function isFolderLocked(folder: UserFolder) {
  return folder.locked === 1;
}

export function isCourseBookmark(bookmark: UserBookmark): boolean {
  return (
    !!bookmark.course_id &&
    !bookmark.lesson_id &&
    bookmark.old_lesson_id == null
  );
}

export function isInternalBookmark(bookmark: UserBookmark): boolean {
  if (
    bookmark.lesson_id != null &&
    bookmark.lesson_status === PUBLISHED_STATUS
  )
    return true;
  if (
    isCourseBookmark(bookmark) &&
    bookmark.course_status === PUBLISHED_STATUS
  )
    return true;
  return false;
}

export function getDisplayTitle(bm: UserBookmark): string {
  const course = isCourseBookmark(bm);
  if (isInternalBookmark(bm)) {
    return course
      ? (bm.course_title ?? "Course")
      : (bm.lesson_title ?? "Lesson");
  }
  return bm.title?.trim() || (course ? "Course" : "Lesson");
}

export function getBookmarkUrl(bookmark: UserBookmark): string {
  if (!bookmark.url) return "#";
  if (isInternalBookmark(bookmark)) return bookmark.url;
  const baseUrl = URLS.WORDPRESS_BASE;
  const url = bookmark.url.startsWith("http")
    ? bookmark.url
    : `${baseUrl}${bookmark.url.startsWith("/") ? "" : "/"}${bookmark.url}`;
  return url.endsWith("/") ? url : `${url}/`;
}

export function extractBookmark(payload: unknown): UserBookmark | undefined {
  if (!payload || typeof payload !== "object") return undefined;
  const p = payload as { item?: UserBookmark; id?: number };
  if (p.item?.id != null) return p.item;
  if (typeof p.id === "number") return p as UserBookmark;
  return undefined;
}

export function sameBookmarkContent(a: UserBookmark, b: UserBookmark): boolean {
  if (a.lesson_id != null && b.lesson_id != null) {
    return Number(a.lesson_id) === Number(b.lesson_id);
  }
  if (a.old_lesson_id != null && b.old_lesson_id != null) {
    return Number(a.old_lesson_id) === Number(b.old_lesson_id);
  }
  if (
    a.course_id != null &&
    b.course_id != null &&
    a.lesson_id == null &&
    b.lesson_id == null &&
    a.old_lesson_id == null &&
    b.old_lesson_id == null
  ) {
    return Number(a.course_id) === Number(b.course_id);
  }
  return false;
}

export function receiverInsertIndex(payload: unknown): number | undefined {
  if (!payload || typeof payload !== "object") return undefined;
  const p = payload as { index?: number; originalIndex?: number };
  if (typeof p.index === "number") return p.index;
  if (typeof p.originalIndex === "number") return p.originalIndex;
  return undefined;
}

export function sameBookmarkOrder(a: UserBookmark[], b: UserBookmark[]) {
  if (a.length !== b.length) return false;
  return a.every((item, i) => item.id === b[i]?.id);
}

export function isBookmarkInFolder(
  list: UserBookmark[] | undefined,
  bookmarkId: number,
): boolean {
  return (list ?? []).some((b) => Number(b.id) === Number(bookmarkId));
}

export function buildBookmarkFolderIndex(
  bookmarks: Record<number, UserBookmark[]>,
): Map<number, number> {
  const map = new Map<number, number>();
  for (const [key, list] of Object.entries(bookmarks)) {
    const folderId = Number(key);
    for (const b of list ?? []) {
      map.set(Number(b.id), folderId);
    }
  }
  return map;
}

/** In-place index update — avoids full rebuild on move/remove */
export function patchBookmarkFolderIndex(
  map: Map<number, number>,
  bookmarkId: number,
  folderId: number | null,
) {
  if (folderId == null) map.delete(bookmarkId);
  else map.set(bookmarkId, folderId);
}

export function hapticLight() {
  if (Platform.OS === "web") return;
  void Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light).catch(() => {});
}
